import { graphql } from 'gql.tada';
import { useQuery, type WatchQueryFetchPolicy } from '@apollo/client';
import { HouseholdChangeRequestFragment } from '~/pages/ParentHousehold/HouseholdChangeRequestFragment';

const MyFamilyQuery = graphql.persisted(
  "MyFamily",
  graphql(`
    query MyFamily($schoolId: ID!, $schoolYearId: ID!, $familyId: ID) {
      currentUser { id gradePostedEmailOptIn }
      school(id: $schoolId) {
        id
        parentAlertsSetting {
          invoiceOverdue
          invoiceDue
          enrollmentAction
          missingWork
          lowGrade
          lowGradeFloorLetter
          lowGradeFloorPercentage
        }
      }
      myFamily(schoolId: $schoolId, familyId: $familyId) {
        id
        name
        slug
        guardians {
          person { id firstName lastName email phone hasUserAccount }
          isPrimary
        }
        children {
          person {
            id
            firstName
            lastName
            fullName
            dateOfBirth
            studentEnrollmentForYear(schoolYearId: $schoolYearId) {
              id
              state
              enrollmentKind
              gradeLevel
            }
          }
        }
        emergencyContacts { name phone relationship }
        authorizedPickup { name phone relationship }
        churchAffiliation { churchName pastorName churchEmail }
        address { street street2 city state zip country }
        pendingHouseholdChangeRequest { ...HouseholdChangeRequestFragment }
        lastReviewedHouseholdChangeRequest { ...HouseholdChangeRequestFragment }
        openInvoices {
          invoiceId
          displayStatus
          amountCents
          dueDate
          memo
          schoolYear { id name slug }
        }
        yearsNeedingYou {
          schoolYear { id name slug }
          access
          checklist {
            id
            waitingOnFamily
            progress { done total waitingOn }
            items { id title personName state }
          }
        }
        familyEnrollmentForYear(schoolYearId: $schoolYearId) {
          id
          state
          access
          accessSource
          checklist {
            id
            completedAt
            waitingOnFamily
            progress { done total waitingOn }
            items { id title personName state lockedReason feeStatus keptAsItIs }
            completedItems { id title personName completedAt }
          }
          enrollmentKind
          inviteStatus
          isInquiry
          inviteToken
          studentCount
          submittedAt
          approvedAt
          packetSentAt
          packetSubmittedAt
          createdAt
          updatedAt
          studentEnrollments {
            id
            state
            enrollmentKind
            gradeLevel
            person { id firstName lastName }
          }
        }
      }
    }
  `, [HouseholdChangeRequestFragment])
);

export { MyFamilyQuery };

export function useMyFamilyQuery(
  schoolId: string | undefined,
  schoolYearId: string | undefined,
  familyId?: string,
  options: { fetchPolicy?: WatchQueryFetchPolicy } = {}
) {
  return useQuery(MyFamilyQuery, {
    variables: { schoolId: schoolId!, schoolYearId: schoolYearId || '', familyId: familyId ?? null },
    skip: !schoolId || !schoolYearId,
    ...options,
  });
}
